import { css } from '@emotion/react';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  MultiStep,
  StepButtonProps,
  StepCloseButton,
  StepFooterProps,
  StepHeaderProps,
  StepProps,
} from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../../collect-payment-multi.context';
import { PAYMENT_MULTI_STEPS } from '../../utils/steps';

const styles = {
  default: css`
    row-gap: 0;

    .step__header,
    .step__footer {
      padding: ${theme.spacing(3)};
    }

    .step__body {
      padding: 0 ${theme.spacing(3)};
    }
  `,
  new: css`
    .step__header {
      border-radius: ${theme.borderRadius.medium};
      background: linear-gradient(0.25turn, ${theme.colors.primary70}, ${theme.colors.primary50});
      padding-top: ${theme.spacing(2)};
      padding-bottom: ${theme.spacing(2)};
      border-radius: ${theme.borderRadius.medium} ${theme.borderRadius.medium} 0px 0px;
      button:hover {
        background: ${theme.colors.primary70};
      }
    }

    .step__header-title,
    .step___header-action button svg {
      color: white;
    }
  `,
  legacy: css``,

  defaultFooter: css`
    .step__body {
      margin-bottom: ${theme.spacing(3)};
    }
  `,
  fullscreenModalFooter: css`
    @media screen and (max-width: ${breakpoints.small.max}px) {
      justify-content: space-between;
    }
  `,
  fullscreenModalStep: (topOffset = 0) => css`
    &.step {
      max-height: calc(100vh - ${topOffset}px);
    }
  `,
};

export const StyledStep = ({
  header,
  components,
  children,
  hasInternalFooter = false,
  ...props
}: StepProps & { hasInternalFooter?: boolean }) => {
  const { Header, Footer, ...cs } = components || {};

  const {
    multiStep: { activeStep },
    isResponsiveMode,
    topOffset,
  } = useCollectPaymentMultiContext();

  const CustomHeader = Header || StyledHeader;
  const CustomFooter = Footer || (isResponsiveMode ? StyledFullscreenModalFooter : undefined);

  const blueHeaderSteps: string[] = [PAYMENT_MULTI_STEPS.create, PAYMENT_MULTI_STEPS.paymentMethods];
  return (
    <MultiStep.Step
      {...props}
      header={header}
      components={{ ...cs, Header: CustomHeader, Footer: CustomFooter }}
      css={[
        styles.default,
        !isResponsiveMode && (blueHeaderSteps.includes(activeStep) ? styles.new : styles.legacy),
        hasInternalFooter && styles.defaultFooter,
        isResponsiveMode && styles.fullscreenModalStep(topOffset),
      ]}
    >
      {children}
    </MultiStep.Step>
  );
};

const StyledHeader = ({ components, ...props }: StepHeaderProps) => {
  const { Action = StyledAction, ...cs } = components || {};

  return (
    <MultiStep.Step.Header
      {...props}
      components={{
        ...cs,
        Action,
      }}
    />
  );
};

const StyledAction = (props: StepButtonProps) => {
  return <StepCloseButton {...props} size='small' />;
};

const StyledFullscreenModalFooter = (props: StepFooterProps) => {
  return <MultiStep.Step.Footer {...props} css={styles.fullscreenModalFooter} />;
};
